import { toNumber } from '@jarvis.oddle/core/tools/number'
import { isEmpty } from '@jarvis.oddle/core/tools/string'

const discountType = {
  amount: 'AMOUNT',
  percent: 'PERCENT'
}

const getVoucherMetadata = (voucher) => {
  try {
    return JSON.parse(JSON.stringify(voucher.metadata || {}))
  } catch (_) {
    return {}
  }
}

const getVoucherTitleFromVoucherify = (voucher) => {
  try {
    return voucher.additional_info || voucher.campaign
  } catch (_) {
    return ''
  }
}

const getVoucherTitleFromOMS = (voucher) => {
  try {
    return voucher.voucherTitle || voucher.additionalInfo || voucher.campaignName
  } catch (_) {
    return ''
  }
}

/**
 * accept both OMS voucher object and voucherify voucher object
 */
export const getVoucherTitle = (voucher) => {
  const voucherMetadata = getVoucherMetadata(voucher)
  return voucherMetadata.title || getVoucherTitleFromOMS(voucher) || getVoucherTitleFromVoucherify(voucher) || ''
}

const getVoucherRedeemedAmountFromVoucherify = (voucher) => {
  try {
    return toNumber((voucher.order || {}).total_discount_amount)
  } catch (_) {
    return 0
  }
}

const getVoucherRedeemedAmountFromOMS = (voucher) => {
  try {
    return toNumber(voucher.redeemedAmount)
  } catch (_) {
    return 0
  }
}

/**
 * accept both OMS voucher object and voucherify voucher object
 */
export const getVoucherRedeemedAmount = (voucher) => {
  return getVoucherRedeemedAmountFromOMS(voucher) || getVoucherRedeemedAmountFromVoucherify(voucher)
}

export const getVoucherDescription = (voucher) => {
  try {
    const voucherMetadata = getVoucherMetadata(voucher)
    return voucherMetadata.description
  } catch (_) {
    return ''
  }
}

export const getVoucherCode = (voucher) => {
  try {
    return voucher.code
  } catch (_) {
    return ''
  }
}

export const getVoucherType = (voucher) => {
  try {
    const voucherMetadata = getVoucherMetadata(voucher)
    return voucherMetadata.type
  } catch (_) {
    return undefined
  }
}

export const getVoucherReward = (voucher, formatMoney) => {
  try {
    if (voucher.discount.type === discountType.amount) {
      return formatMoney(voucher.discount.amount_off)
    }

    if (voucher.discount.type === discountType.percent) {
      return `${voucher.discount.percent_off}%`
    }

    return ''
  } catch (_) {
    return ''
  }
}

/**
 * accept both OMS voucher object and voucherify voucher object
 */
export const createOddleShopVoucherSummary = (voucher) => {
  return {
    voucherType: getVoucherType(voucher),
    voucherTitle: getVoucherTitle(voucher),
    redeemedAmount: getVoucherRedeemedAmount(voucher)
  }
}

/**
 *
 * @param checkoutMetaData  @@checkout.meta.data object retrieved from the redux store
 * @param adjustDecimal this arg will multiply the item price by 100 to precisely represent 2 decimal places
 *                      as required by voucherify https://docs.voucherify.io/reference#validate-voucher
 * @param isSubmitCheckout the flag to know this function is being used to POST checkout
 */
export const createValidationPayload = (checkoutMetaData, adjustDecimal = false, isSubmitCheckout = false) => {
  const { customer, storeOrder, meta } = checkoutMetaData || {}
  const { oddlePassFirstOrder, oddlePassFirstMenuOrder } = meta || {}
  try {
    return {
      customer: {
        source_id: customer.ocaId,
        email: customer.email
      },
      order: {
        source_id: storeOrder.id,
        amount: storeOrder.total * (adjustDecimal ? 100 : 1),
        items: (storeOrder.orderItems || []).map(item => ({
          product_id: item.itemId,
          quantity: item.quantity,
          price: item.price * (adjustDecimal ? 100 : 1)
        })),
        metadata: {
          menuId: storeOrder.menuId,
          isPickup: storeOrder.isPickup, // true (pickup order) or false (delivery order)
          oddlePassFirstOrder,
          oddlePassFirstMenuOrder,
          fulfilmentDate: new Date(`${storeOrder.collectionDate} ${storeOrder.collectionTime.split(' to ')[0]}`).toISOString(), // format in ISO 8601 norms eg. 2020-03-11T09:00:00.000Z
          submissionDate: new Date(storeOrder.orderDateRank).toISOString(), // format in ISO 8601 norms eg. 2020-03-11T09:00:00.000Z
          cardBin: isSubmitCheckout ? null : 'bypassValidation'
        }
      }
    }
  } catch (_) {
    return {}
  }
}

/**
 *
 * @param checkoutMetaData @@checkout.meta.data object retrieved from the redux store
 * @param selectedVoucher @@checkout.selectedVoucher.data object retrieved from the redux store
 */
export const createCheckoutPayload = (checkoutMetaData, selectedVoucher) => {
  try {
    return {
      voucher: {
        code: selectedVoucher.code,
        voucherValidation: {
          // adjustDecimal is set to true as OMS does not auto adjust them
          ...(
            createValidationPayload(checkoutMetaData, true, true)
          ),
          session: selectedVoucher.session
        }
      }
    }
  } catch (_) {
    return {}
  }
}

export const getHideVoucherRedemptionLimit = (voucher) => {
  try {
    if (isEmpty((voucher.redemption || {}).quantity)) return true
    const voucherMetadata = getVoucherMetadata(voucher)
    return ['true', true].includes(voucherMetadata.hideVoucherRedemptionLimit)
  } catch (_) {
    return false
  }
}
